import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {Box, Typography} from '@mui/material';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import {
  useUploadReProductionSeedFile,
  useGetReProductionSeedPath,
  useReProductionSeedPageCount,
} from '../../../../../hooks/productions/useProductions';
import {ToastType} from '../../../../../shared/constants';
import ArrToast from '../../../../common/arr-toast/ArrToast';
import {ReactComponent as CloseToast} from '../../../../../assets/images/close-toast.svg';
import {t} from 'i18next';
import '../../../create-new-production/generate/ErrorModal/ReplaceSeedPdfFlyout.scss';

interface ReProductionReplaceSeedPdfFlyoutProps {
  isOpen: boolean;
  documentId: number;
  productionId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const ReProductionReplaceSeedPdfFlyout: React.FC<
  ReProductionReplaceSeedPdfFlyoutProps
> = ({isOpen, documentId, productionId, onClose, onSuccess}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [newSeedFilePath, setNewSeedFilePath] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {data: seedPathData} = useGetReProductionSeedPath(
    {
      documentId: documentId,
      productionId: productionId,
    },
    isOpen && !!documentId
  );

  const _seedFilePath = seedPathData?.seedFilePath || '';
  const _pageCount = seedPathData?.pageCount || 0;

  const uploadSeedFileMutation = useUploadReProductionSeedFile(
    (data) => {
      if (data.success === false) {
        const errorMessage =
          data.message ||
          t(
            'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.uploadFailed'
          );
        setToastMessage(errorMessage);
        setToastStatus([ToastType.Error]);
        setShowToast(true);
        setNewSeedFilePath('');
      } else if (data.success === true && data.seedFilePath) {
        setNewSeedFilePath(data.seedFilePath);
      }
    },
    (error) => {
      const errorMessage =
        error?.message ||
        t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.uploadFailed'
        );
      setToastMessage(errorMessage);
      setToastStatus([ToastType.Error]);
      setShowToast(true);
      setNewSeedFilePath('');
    }
  );

  const seedPageCountMutation = useReProductionSeedPageCount(
    (data) => {
      if (data.success === false) {
        setToastMessage(data.message);
        setToastStatus([ToastType.Error]);
        setShowToast(true);
      } else if (data.success === true) {
        setToastMessage(data.message);
        setToastStatus([ToastType.Success]);
        setShowToast(true);
        onSuccess?.();
        onClose();
      }
    },
    (error) => {
      const errorMessage =
        error?.message || 'Failed to validate seed file page count';
      setToastMessage(errorMessage);
      setToastStatus([ToastType.Error]);
      setShowToast(true);
    }
  );

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setNewSeedFilePath('');
      uploadSeedFileMutation.mutate(file);
    }
  };

  const handleSavePdf = () => {
    if (selectedFile) {
      setShowConfirmationModal(true);
    }
  };

  const handleConfirmContinue = () => {
    setShowConfirmationModal(false);
    seedPageCountMutation.mutate({
      payload: {
        pagecount: _pageCount,
        seedFilePath: _seedFilePath,
        newSeedFilePath: newSeedFilePath,
        documentID: documentId,
        productionID: productionId,
      },
    });
  };

  const handleConfirmCancel = () => {
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setNewSeedFilePath('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleClose = () => {
    handleCancel();
  };

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        const drawerElement = document.querySelector(
          '.arr-panel.replace-seed-pdf-flyout'
        );
        if (drawerElement) {
          const modalRoot = drawerElement.closest(
            '.MuiModal-root'
          ) as HTMLElement;
          if (modalRoot) {
            modalRoot.style.zIndex = '10000';
          }

          const drawerRoot = drawerElement.closest(
            '.MuiDrawer-root'
          ) as HTMLElement;
          if (drawerRoot) {
            drawerRoot.style.zIndex = '10000';
          }
        }

        const allModals = document.querySelectorAll('.MuiModal-root');
        allModals.forEach((modal) => {
          if (modal.querySelector('.replace-seed-pdf-flyout')) {
            (modal as HTMLElement).style.zIndex = '10000';
          }
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  useEffect(() => {
    if (showToast) {
      const timeoutId = setTimeout(() => {
        const toastContainer = document.querySelector(
          '.toast-notification__container'
        );
        if (toastContainer) {
          (toastContainer as HTMLElement).style.zIndex = '10002';
          (toastContainer as HTMLElement).style.position = 'fixed';
        }
        const snackbarRoots = document.querySelectorAll('.MuiSnackbar-root');
        snackbarRoots.forEach((snackbar) => {
          (snackbar as HTMLElement).style.zIndex = '10002';
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [showToast]);

  useEffect(() => {
    if (showConfirmationModal) {
      const timeoutId = setTimeout(() => {
        const modalElement = document.querySelector(
          '.replace-seed-pdf-confirmation-modal'
        );
        if (modalElement) {
          const modalRoot = modalElement.closest(
            '.MuiModal-root'
          ) as HTMLElement;
          if (modalRoot) {
            modalRoot.style.zIndex = '10001';
          }

          const arrModal = modalElement.closest('.arr-modal') as HTMLElement;
          if (arrModal) {
            arrModal.style.zIndex = '10001';
          }
        }

        const allModals = document.querySelectorAll('.MuiModal-root');
        allModals.forEach((modal) => {
          if (modal.querySelector('.replace-seed-pdf-confirmation-modal')) {
            (modal as HTMLElement).style.zIndex = '10001';
          }
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [showConfirmationModal]);

  return (
    <>
      <ArrDrawer
        isOpen={isOpen}
        onClose={handleClose}
        showButton={false}
        showHeader={false}
        size="small"
        side="right"
        className="replace-seed-pdf-flyout"
        ariaLabel={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.title'
        )}
      >
        <Box className="replace-seed-pdf-flyout__header">
          <Typography
            className="replace-seed-pdf-flyout__title typo-text headline3_size"
            data-testid="replace-seed-pdf-flyout-title"
          >
            {t(
              'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.title'
            )}
          </Typography>
          <Box className="replace-seed-pdf-flyout__header-actions">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={handleCancel}
              dataTestId="replace-seed-pdf-flyout-cancel-button"
            >
              {t(
                'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.cancel'
              )}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="medium"
              onClick={handleSavePdf}
              disabled={
                !selectedFile ||
                !newSeedFilePath ||
                uploadSeedFileMutation.isPending ||
                seedPageCountMutation.isPending
              }
              dataTestId="save-pdf-button"
            >
              {t(
                'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.savePdf'
              )}
            </ArrButton>
          </Box>
        </Box>

        <Box className="replace-seed-pdf-flyout__content">
          <Box className="replace-seed-pdf-flyout__file-section">
            <Box className="replace-seed-pdf-flyout__file-header">
              <Typography
                className="typo-text subhead5_size replace-seed-pdf-flyout__file-label"
                data-testid="replace-seed-pdf-flyout-select-file"
              >
                {t(
                  'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.selectFile'
                )}
              </Typography>
              <ArrButton
                variant="primary"
                size="small"
                className="replace-seed-pdf-flyout__select-file-btn typo-text buttonSmall_size"
                onClick={handleSelectFileClick}
                dataTestId="select-file-button"
              >
                {t(
                  'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.selectFile'
                )}
              </ArrButton>
            </Box>
            <Box className="replace-seed-pdf-flyout__file-display">
              {selectedFile ? (
                <Typography
                  className="typo-text body4_size replace-seed-pdf-flyout__file-name"
                  data-testid="file-display"
                >
                  {selectedFile.name}
                </Typography>
              ) : (
                <Typography
                  className="typo-text body4_size replace-seed-pdf-flyout__file-placeholder"
                  data-testid="file-display"
                >
                  {t(
                    'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.noFileChosen'
                  )}
                </Typography>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              style={{display: 'none'}}
              onChange={handleFileChange}
              data-testid="file-input"
            />
          </Box>
        </Box>
      </ArrDrawer>
      {showToast &&
        (() => {
          const currentToastType = toastStatus[0];
          let toastType: 'success' | 'error' | 'warning' = 'error';
          if (currentToastType === ToastType.Success) {
            toastType = 'success';
          } else if (currentToastType === ToastType.Warning) {
            toastType = 'warning';
          }
          const toastTitle =
            currentToastType === ToastType.Success
              ? t('common.success')
              : t('common.error');
          return ReactDOM.createPortal(
            <ArrToast
              id="replace-seed-pdf-toast"
              data-testid="toast-notification"
              title={toastTitle}
              message={toastMessage}
              type={toastType}
              onClose={() => {
                setShowToast(false);
              }}
              dismissable
              closeIcon={<CloseToast />}
              position="bottom-right"
            />,
            document.body
          );
        })()}
      <ArrModal
        showModal={showConfirmationModal}
        onClose={handleConfirmCancel}
        headerTitle={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.title'
        )}
        headerTitleClassName="headline3_size"
        description={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.description'
        )}
        ariaLabel={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.title'
        )}
        ariaDescription={t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.description'
        )}
        showClose={false}
        hideDefaultActions={true}
        className="replace-seed-pdf-confirmation-modal"
        dataTestId="replace-seed-pdf-confirmation-modal"
      >
        <Box className="replace-seed-pdf-confirmation-modal__buttons">
          <ArrButton
            variant="primary"
            size="large"
            onClick={handleConfirmContinue}
            disabled={
              !newSeedFilePath ||
              uploadSeedFileMutation.isPending ||
              seedPageCountMutation.isPending
            }
            loading={seedPageCountMutation.isPending}
            dataTestId="yes-continue-button"
          >
            {t(
              'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.yesContinue'
            )}
          </ArrButton>
          <ArrButton
            variant="secondary"
            size="large"
            onClick={handleConfirmCancel}
            disabled={
              uploadSeedFileMutation.isPending ||
              seedPageCountMutation.isPending
            }
            dataTestId="no-cancel-button"
          >
            {t(
              'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.confirmationModal.noCancel'
            )}
          </ArrButton>
        </Box>
      </ArrModal>
    </>
  );
};

export default ReProductionReplaceSeedPdfFlyout;
